import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { HeaderComponent } from "Components/Shared";
import { Button, Row, Col } from "Components/Core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFilePdf,
  faFileImage,
  faFileAlt,
} from "@fortawesome/free-solid-svg-icons";
import {
  getCoachVerificationRequest,
  updateCoachVerificationRequests,
} from "Api/Pages/CoachesApi";

const formatYM = (months?: number) => {
  if (!months && months !== 0) return "-";
  const years = Math.floor(months / 12);
  const rem = months % 12;
  const yStr = years ? `${years} Y` : "";
  const mStr = rem ? `${rem} Mo` : years ? "" : "0 Mo";
  return [yStr, mStr].filter(Boolean).join(" ");
};

const VerificationRequestDetail = () => {
  const { id } = useParams();
  const [data, setData] = useState<any>(null);
  const [previewUrl, setPreviewUrl] = useState<string>("");
  const [showPreview, setShowPreview] = useState<boolean>(false);
  const [initialStatus, setInitialStatus] = useState<string | undefined>(
    undefined
  );
  // Single page layout (no tabs)

  useEffect(() => {
    const run = async () => {
      if (!id) return;
      const resp = await getCoachVerificationRequest(Number(id));
      // capture initial status first to avoid transient mismatches
      setInitialStatus(resp?.verification_request_status);
      setData(resp);
    };
    run();
  }, [id]);

  const infoBlocks = useMemo(() => {
    if (!data) return [] as any[];
    const contact =
      data.country_code && data.contact_number
        ? `+${data.country_code} ${data.contact_number}`
        : data.contact_number || "-";
    return [
      { label: "Coach Name", value: data.name },
      { label: "Email ID", value: data.email || "-" },
      { label: "Contact No.", value: contact },
      {
        label: "Experience",
        value: formatYM(data.verification_request_experience_in_months),
      },
      { label: "Bio", value: data.bio || "-" },
    ];
  }, [data]);

  const onPreview = (url: string) => {
    if (!url) return;
    setPreviewUrl(url);
    setShowPreview(true);
  };

  // Download disabled per requirement; only Preview is supported for now.

  const getDisplayNameFromUrl = (url: string) => {
    try {
      const parts = url.split("/");
      const last = parts[parts.length - 1] || url;
      return decodeURIComponent(last);
    } catch (_) {
      return url;
    }
  };

  const isImage = (url: string) =>
    /\.(png|jpe?g|gif|webp|heic|bmp|svg)$/i.test(url);
  const isPdf = (url: string) => /\.(pdf)$/i.test(url);
  const fileIcon = (nameOrUrl: string) => {
    if (isPdf(nameOrUrl)) return faFilePdf;
    if (isImage(nameOrUrl)) return faFileImage;
    return faFileAlt;
  };

  // Close on ESC and on clicking backdrop
  useEffect(() => {
    if (!showPreview) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setShowPreview(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [showPreview]);

  return (
    <div className="ss-page-content d-flex flex-column flex-1 scroll-y bg-white">
      <HeaderComponent title="Coach Verification" />
      <div className="ss-page-body d-flex flex-column flex-1 scroll-y">
        {/* Actions: Approve/Reject Eligibility based on status */}
        <div className="d-flex justify-content-end px-4 py-3 gap-2">
          {data?.verification_request_status === "pending" && (
            <>
              <Button
                variant="success"
                onClick={async () => {
                  if (!id) return;
                  await updateCoachVerificationRequests(
                    [Number(id)],
                    "approved"
                  );
                  setData({ ...data, verification_request_status: "approved" });
                }}
              >
                Approve Eligibility
              </Button>
              <Button
                variant="danger"
                onClick={async () => {
                  if (!id) return;
                  await updateCoachVerificationRequests(
                    [Number(id)],
                    "rejected"
                  );
                  setData({ ...data, verification_request_status: "rejected" });
                }}
              >
                Reject Eligibility
              </Button>
            </>
          )}
          {data?.verification_request_status === "rejected" &&
            (initialStatus === "rejected" ? (
              <Button
                variant="success"
                onClick={async () => {
                  if (!id) return;
                  await updateCoachVerificationRequests(
                    [Number(id)],
                    "approved"
                  );
                  setData({ ...data, verification_request_status: "approved" });
                }}
              >
                Approve Eligibility
              </Button>
            ) : (
              <span className="badge bg-danger align-self-center">
                Rejected
              </span>
            ))}
          {data?.verification_request_status === "approved" &&
            (initialStatus === "approved" ? (
              <Button
                variant="danger"
                onClick={async () => {
                  if (!id) return;
                  await updateCoachVerificationRequests(
                    [Number(id)],
                    "rejected"
                  );
                  setData({ ...data, verification_request_status: "rejected" });
                }}
              >
                Reject Eligibility
              </Button>
            ) : (
              <span className="badge bg-success align-self-center">
                Approved
              </span>
            ))}
        </div>
        {showPreview && (
          <div
            className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
            style={{ background: "rgba(0,0,0,0.6)", zIndex: 1050 }}
          >
            <div
              className="bg-white shadow rounded"
              style={{
                width: "80%",
                maxWidth: 900,
                height: "80%",
                maxHeight: 900,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom">
                <span className="fw-bold">Preview</span>
                <div className="d-flex gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setShowPreview(false)}
                  >
                    Close
                  </Button>
                </div>
              </div>
              <div style={{ flex: 1 }}>
                <iframe
                  title="cert-preview"
                  src={previewUrl}
                  style={{ border: 0, width: "100%", height: "100%" }}
                />
              </div>
            </div>
          </div>
        )}
        <div className="ss-tab-content p-4">
          <Row className="g-4">
            {/* First part: Info and Bio */}
            <Col lg={6} className="d-grid gap-3">
              <div className="border rounded p-3">
                <p className="fs-6 fw-bold mb-3">Coach Info</p>
                <div className="d-flex align-items-center gap-3">
                  {data?.image && (
                    <img
                      src={data.image}
                      alt="Profile"
                      className="rounded-circle"
                      style={{ width: 48, height: 48, objectFit: "cover" }}
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                        const sibling =
                          (e.currentTarget.nextElementSibling as HTMLElement) ||
                          null;
                        if (sibling) sibling.classList.remove("d-none");
                      }}
                    />
                  )}
                  <div
                    className={`rounded-circle bg-secondary text-white d-flex align-items-center justify-content-center ${
                      data?.image ? "d-none" : ""
                    }`}
                    style={{ width: 48, height: 48 }}
                  >
                    <span className="fw-bold">
                      {(data?.name || "")
                        .split(" ")
                        .map((p: string) => p[0])
                        .join("")
                        .slice(0, 2)
                        .toUpperCase()}
                    </span>
                  </div>
                  <div className="d-grid">
                    <span className="fw-bold">{data?.name || "-"}</span>
                    {data?.email && (
                      <span className="text-muted fs-xs">{data.email}</span>
                    )}
                    {data?.country_code && data?.contact_number && (
                      <div className="mt-1">
                        <span className="fw-bold fs-sm">
                          {data.country_code} {data.contact_number}
                        </span>
                      </div>
                    )}
                    {data?.verification_request_experience_in_months && (
                      <span
                        className="badge bg-light text-dark border mt-2"
                        style={{ width: "fit-content" }}
                      >
                        {`Experience: ${formatYM(
                          data.verification_request_experience_in_months
                        )}`}
                      </span>
                    )}
                  </div>
                </div>
                {/* Additional info fields (exclude Coach Name, Email ID, Contact No., Experience, Bio) */}
                <div className="mt-3">
                  {infoBlocks
                    .filter(
                      (b) =>
                        ![
                          "Coach Name",
                          "Email ID",
                          "Contact No.",
                          "Experience",
                          "Bio",
                        ].includes(b.label)
                    )
                    .map((b) => (
                      <div key={b.label} className="mb-2">
                        <label className="fs-xs text-muted">{b.label}</label>
                        <p className="fs-sm fw-bold mb-0">{b.value}</p>
                      </div>
                    ))}
                </div>
              </div>
              {/* Bio as separate component/card */}
              <div className="border rounded p-3">
                <p className="fs-6 fw-bold mb-2">Bio</p>
                <p className="fs-sm mb-0">{data?.bio || "-"}</p>
              </div>
            </Col>

            {/* Second part: Sports */}
            <Col lg={6} className="d-grid gap-3">
              {!!data?.coach_sports?.length && (
                <div className="border rounded p-3 d-grid gap-2">
                  <p className="fs-6 fw-bold mb-0">Sports</p>
                  {data.coach_sports.map((s: any, idx: number) => (
                    <div
                      key={idx}
                      className="d-flex align-items-center justify-content-between border rounded px-3 py-2"
                    >
                      <div className="d-flex align-items-center gap-2">
                        <span className="fw-bold fs-sm">{s.sport?.name}</span>
                      </div>
                      <span className="badge bg-primary">{s.level || "-"}</span>
                    </div>
                  ))}
                </div>
              )}
            </Col>
          </Row>

          {/* Third part: Certifications - Full width */}
          <Row className="mt-4">
            <Col lg={12}>
              <div className="border rounded p-3 d-grid gap-2">
                <p className="fs-6 fw-bold mb-0">Certifications</p>
                {data?.certifications?.length ? (
                  data.certifications.map((item: any, i: number) => {
                    const url = typeof item === "string" ? item : item?.url;
                    const name =
                      typeof item === "string"
                        ? getDisplayNameFromUrl(item)
                        : item?.filename ||
                          getDisplayNameFromUrl(item?.url || "");
                    return (
                      <div
                        key={i}
                        className="d-flex align-items-center justify-content-between border p-2 rounded"
                      >
                        <div
                          className="d-flex align-items-center gap-2 text-truncate"
                          title={name}
                          style={{ maxWidth: "80%" }}
                        >
                          <FontAwesomeIcon
                            icon={fileIcon(name || url)}
                            className={
                              isPdf(name || url)
                                ? "text-danger"
                                : "text-secondary"
                            }
                          />
                          <span className="text-break text-truncate">
                            {name}
                          </span>
                        </div>
                        <div className="d-flex gap-2">
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => onPreview(url)}
                          >
                            Preview
                          </Button>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <p className="fs-sm mb-0">No certifications available.</p>
                )}
              </div>
            </Col>
          </Row>
        </div>
      </div>
    </div>
  );
};

export default VerificationRequestDetail;
